// Shared presentation helpers for the email engine surfaces (Session ASO): the Email engine page, its message detail and
// the "Email engine" panel on an enquiry. One home for how an outcome reads and what colour its pill is, so the page and
// the enquiry mirror cannot disagree about one message. Plain English throughout (never the raw outcome key).
import type { EmailEngineCheck, EmailEngineMessageRow, InboundEmailOutcome } from "../../lib/api";
import type { PillTone } from "../../components/StatusPill";
import { statusEntry } from "../../lib/statusRegistry";

export const OUTCOME_LABEL: Record<InboundEmailOutcome, string> = {
  pending: "Being decided",
  drafted: "Drafted",
  needs_you: "Needs you",
  no_reply_needed: "No reply needed",
};

/** The pill a message shows: a message past the deadline with no outcome is a FAULT, in red, whatever else it says. */
export function outcomeDisplay(m: Pick<EmailEngineMessageRow, "outcome" | "overdue">): { word: string; tone: PillTone } {
  if (m.overdue) return { word: "Overdue", tone: "red" };
  // The WORD is this page's own plain English (OUTCOME_LABEL); the TONE is the registry's single answer.
  return { word: OUTCOME_LABEL[m.outcome] ?? m.outcome, tone: statusEntry("emailOutcome", m.outcome).tone };
}

export const CHECK_LABEL: Record<EmailEngineCheck, string> = {
  figures: "Prices and figures",
  references: "References",
  dates: "Dates",
  style: "House style",
  phone: "Phone number",
  address: "Address",
  personal_name: "Personal name",
  promise: "Promises",
  attachments: "Attachments",
  house_words: "House words",
  loler: "LOLER examinations",
  tier: "Cover tier",
  privacy: "Another customer's details",
  sentence_gate: "A statement the records do not support",
  // Session BCR2 (BCR1 rule R1): the draft asked for something the sender had already given, or promised to send a document
  // the conversation shows we already sent.
  already_given: "Asked for something already given",
  // Session BDE: never advise calling the fire brigade or another emergency service.
  emergency_services: "Advised calling an emergency service",
  visit_window: "Promised an exact time, or a window outside 8am to 4pm",
};

export const EDIT_KIND_LABEL: Record<string, string> = {
  unchanged: "Sent unchanged",
  small_edits: "Sent with small edits",
  heavily_rewritten: "Heavily rewritten before sending",
  discarded: "Discarded (a different reply was sent)",
};

export const ATTACHMENT_LABEL: Record<string, string> = {
  scheduleOfFees: "Schedule of Fees",
  termsAndConditions: "Terms and Conditions",
};

export function senderLine(m: Pick<EmailEngineMessageRow, "from" | "fromName">): string {
  if (m.fromName && m.from) return `${m.fromName} <${m.from}>`;
  return m.fromName || m.from || "Unknown sender";
}

export function gmailThreadHref(threadId: string): string {
  return `https://mail.google.com/mail/u/0/#all/${encodeURIComponent(threadId)}`;
}

/**
 * Session AUA (email system Phase 3): where a draft's wording came from, as every surface that shows a draft labels it.
 * "Free draft" when no approved template fitted (the fallback the engine writes itself); "From template <name>, version N"
 * when an approved template was filled. Null when nothing says (no draft, or an older server). One home, so the Email
 * engine page, the message detail, the draft preview and the Enquiries pages cannot word it differently.
 */
export function draftSourceLabel(d: {
  kind?: "template" | "free" | "agent" | null;
  templateKey?: string | null;
  templateVersion?: number | null;
  templateTitle?: string | null;
}): string | null {
  if (d.kind === "free") return "Free draft";
  if (d.kind === "agent") return "Investigating agent draft";
  if (d.kind === "template" && d.templateKey) {
    const name = d.templateTitle ? `${d.templateTitle} (${d.templateKey})` : d.templateKey;
    return `From template ${name}${d.templateVersion != null ? `, version ${d.templateVersion}` : ""}`;
  }
  return null;
}

/** The signature every drafted reply carries (Director decision DE-3), named as the server names it. */
export const COMPANY_SIGNATURE_LABEL = "Company signature";
